import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BuscarPecasRow } from '@core/types';
import Layout from '../componentes/Layout';
import Carregando from '../componentes/Carregando';
import Aviso from '../componentes/Aviso';
import {
  aplicarPromocoes, buscarPecas, criarPromocao, encerrarPromocao, fotosDasPecas,
  listarPromocoes, promocoesAbertas, promocoesEncerradas, urlDaFoto,
} from '../lib/consultas';
import { brl, hojeISO, paraNumero, qtd } from '../lib/formato';
import { descontoPct } from '../lib/promocao';
import { podeGerenciarCatalogo, useSessao } from '../lib/sessao';
import { mensagemDeErro } from '../lib/supabase';

// Aba Promoções, v2 (pedido do dono, 17/08/2026): "Adicionar promoção" pelo %,
// com VIGÊNCIA — enquanto dura, o preço do balcão é o promocional (o carimbo
// products.preco_promocional, que a busca e a Venda já consomem) — e HISTÓRICO
// do que já passou. A migração 047 tem o desenho.
//
// O visual pedido, nas palavras dele: cartão com "bordas vermelhas finas da
// cor da Serginho e fundo da cor do fundo" — border-signal fina + bg-ink, não
// o cartao padrão (bg-panel). Pouca poluição: duas abas internas e pronto.

const dataBR = (iso: string) => iso.split('-').reverse().join('/');

export default function Promocoes() {
  const { papel } = useSessao();
  const cliente = useQueryClient();
  const gerencia = podeGerenciarCatalogo(papel);
  const [aba, setAba] = useState<'vigentes' | 'historico'>('vigentes');
  const [criando, setCriando] = useState(false);

  // A vassoura das vigências roda quando a tela abre: fecha o que venceu ontem
  // e carimba a agendada que começou hoje. Falhar é silencioso de propósito —
  // é manutenção; a consulta abaixo mostra o estado que der.
  useEffect(() => {
    aplicarPromocoes().finally(() => {
      cliente.invalidateQueries({ queryKey: ['promocoes'] });
    }).catch(() => undefined);
  }, [cliente]);

  const vigentes = useQuery({
    queryKey: ['promocoes', 'vigentes'],
    queryFn: async () => {
      const [linhas, abertas] = await Promise.all([listarPromocoes(), promocoesAbertas()]);
      const fotos = await fotosDasPecas(linhas.map((l) => l.product_id));
      const fotoPor = new Map<string, { id: string; updated_at: string }>();
      for (const f of fotos) {
        // A principal é a de menor ordem — a lista vem ordenada do banco.
        if (!fotoPor.has(f.product_id)) fotoPor.set(f.product_id, f);
      }
      const promoPor = new Map(abertas.map((p) => [p.product_id, p]));
      return linhas.map((l) => ({
        ...l,
        foto: fotoPor.get(l.product_id) ?? null,
        promo: promoPor.get(l.product_id) ?? null,
      }));
    },
    staleTime: 60_000,
  });

  const historico = useQuery({
    queryKey: ['promocoes', 'historico'],
    queryFn: () => promocoesEncerradas(),
    enabled: aba === 'historico',
  });

  const recarregar = () => {
    cliente.invalidateQueries({ queryKey: ['promocoes'] });
    cliente.invalidateQueries({ queryKey: ['buscar'] });
  };

  const encerrar = useMutation({ mutationFn: encerrarPromocao, onSuccess: recarregar });

  return (
    <Layout
      titulo="Promoções"
      acao={gerencia ? (
        <button type="button" onClick={() => setCriando((v) => !v)}
                className="botao-primario px-3 py-2 text-xs">
          + Promoção
        </button>
      ) : undefined}
    >
      <div className="space-y-4">
        {criando && (
          <NovaPromocao aoFechar={() => setCriando(false)}
                        aoSalvar={() => { setCriando(false); recarregar(); }} />
        )}

        <div className="flex gap-2">
          {([['vigentes', 'Vigentes'], ['historico', 'Histórico']] as const).map(([k, t]) => (
            <button key={k} type="button" onClick={() => setAba(k)} aria-pressed={aba === k}
                    className={`border px-3 py-1 text-xs font-bold ${
                      aba === k ? 'border-signal bg-signal/10' : 'border-edge text-mute'}`}>
              {t}
            </button>
          ))}
        </div>

        {vigentes.error ? <Aviso tipo="erro">{mensagemDeErro(vigentes.error)}</Aviso> : null}
        {encerrar.error ? <Aviso tipo="erro">{mensagemDeErro(encerrar.error)}</Aviso> : null}

        {aba === 'vigentes' && (
          <>
            {vigentes.isLoading && <Carregando texto="Buscando promoções" />}
            {vigentes.data && vigentes.data.length === 0 && (
              <div className="cartao p-6 text-center text-sm text-mute">
                Nenhuma peça em promoção agora.
                {gerencia && <span className="mt-1 block">Toque em <strong>+ Promoção</strong> para colocar uma.</span>}
              </div>
            )}
            <ul className="space-y-2">
              {(vigentes.data ?? []).map((p) => (
                <li key={p.product_id}
                    className="border border-signal/70 bg-ink p-4">
                  <div className="flex items-start gap-3">
                    {p.foto && (
                      <Link to={`/peca/${p.product_id}`} className="shrink-0">
                        <img src={urlDaFoto(p.foto.id, p.foto.updated_at)} alt=""
                             className="size-14 border border-edge object-cover" loading="lazy" />
                      </Link>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-mono text-[0.6875rem] uppercase tracking-wider text-dim">
                        {p.sku}{p.marca ? ` · ${p.marca}` : ''}
                      </p>
                      <Link to={`/peca/${p.product_id}`} className="mt-0.5 block font-bold leading-snug hover:text-signal-soft">
                        {p.descricao}
                      </Link>
                      <p className="mt-1 text-xs text-dim">
                        {p.promo
                          ? <>vale de {dataBR(p.promo.inicio)} até <strong className="text-mute">{dataBR(p.promo.fim)}</strong></>
                          : 'sem vigência (preço posto no cadastro)'}
                        {' · '}
                        <span className={p.quantidade <= 0 ? 'text-erro' : 'text-ok'}>
                          {p.quantidade <= 0 ? 'sem estoque' : `${qtd(p.quantidade)} un`}
                        </span>
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-xs text-dim line-through">{brl(p.preco_balcao)}</p>
                      <p className="text-lg font-black text-paper">{brl(p.preco_promocional)}</p>
                      {(descontoPct(p) ?? 0) > 0 && (
                        <p className="inline-block rounded-md bg-edge px-1.5 py-0.5 text-xs font-bold text-paper">
                          −{descontoPct(p)}%
                        </p>
                      )}
                      {gerencia && p.promo && (
                        <button type="button" disabled={encerrar.isPending}
                                onClick={() => encerrar.mutate(p.promo!.id)}
                                className="mt-1 block w-full text-right text-xs text-dim underline">
                          encerrar
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        {aba === 'historico' && (
          <>
            {historico.isLoading && <Carregando texto="Buscando o histórico" />}
            {historico.data && historico.data.length === 0 && (
              <div className="cartao p-6 text-center text-sm text-mute">
                Nenhuma promoção encerrada ainda — o histórico nasce quando a
                primeira vigência acabar.
              </div>
            )}
            <ul className="space-y-2">
              {(historico.data ?? []).map((h) => (
                <li key={h.id} className="cartao p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-mono text-[0.6875rem] uppercase tracking-wider text-dim">
                        {h.products?.sku ?? '—'}{h.products?.marca ? ` · ${h.products.marca}` : ''}
                      </p>
                      <p className="text-sm font-bold">{h.products?.descricao ?? 'peça removida do catálogo'}</p>
                      <p className="mt-0.5 text-xs text-dim">
                        {dataBR(h.inicio)} a {dataBR(h.fim)}
                        {h.encerrada_em && h.encerrada_em.slice(0, 10) < h.fim
                          ? ' · encerrada antes do fim' : ''}
                        {h.criado_por_nome ? ` · por ${h.criado_por_nome}` : ''}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-xs text-dim line-through">{brl(Number(h.preco_de))}</p>
                      <p className="text-sm font-black">{brl(Number(h.preco_por))}</p>
                      <p className="text-xs text-mute">−{Number(h.desconto_pct)}%</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </Layout>
  );
}

/** O "Adicionar promoção": acha a peça, digita o %, define a vigência. */
function NovaPromocao({ aoFechar, aoSalvar }: { aoFechar: () => void; aoSalvar: () => void }) {
  const [termo, setTermo] = useState('');
  const [peca, setPeca] = useState<BuscarPecasRow | null>(null);
  const [pct, setPct] = useState('');
  const [inicio, setInicio] = useState(hojeISO());
  const [fim, setFim] = useState('');

  const resultados = useQuery({
    queryKey: ['promocoes', 'busca-peca', termo],
    queryFn: () => buscarPecas(termo, 8),
    enabled: !peca && termo.trim().length >= 2,
  });

  const salvar = useMutation({
    mutationFn: () => criarPromocao({
      productId: peca!.product_id,
      descontoPct: paraNumero(pct) ?? 0,
      inicio,
      fim,
    }),
    onSuccess: aoSalvar,
  });

  // A prévia "de X por Y" ANTES de salvar — o banco congela exatamente isto.
  const previa = useMemo(() => {
    const p = paraNumero(pct);
    const balcao = Number(peca?.preco_balcao ?? 0);
    if (!peca || p === null || p <= 0 || p >= 100 || balcao <= 0) return null;
    return { de: balcao, por: Math.round(balcao * (1 - p / 100) * 100) / 100 };
  }, [peca, pct]);

  return (
    <section className="cartao space-y-3 border-signal/50 p-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold">Nova promoção</h2>
        <button type="button" onClick={aoFechar} className="px-2 text-sm text-dim">✕</button>
      </div>

      {!peca ? (
        <div>
          <label className="rotulo" htmlFor="pr-busca">Qual peça?</label>
          <input id="pr-busca" className="campo" autoFocus placeholder="código, nome ou marca"
                 value={termo} onChange={(e) => setTermo(e.target.value)} />
          {(resultados.data ?? []).length > 0 && (
            <ul className="mt-2 divide-y divide-edge border border-edge">
              {(resultados.data ?? []).map((r) => (
                <li key={r.product_id}>
                  <button type="button" className="flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left hover:bg-panel-alt"
                          onClick={() => setPeca(r)}>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-bold">{r.descricao}</span>
                      <span className="font-mono text-xs text-dim">{r.sku}{r.marca ? ` · ${r.marca}` : ''}</span>
                    </span>
                    <span className="shrink-0 font-mono text-sm tabular-nums">{brl(r.preco_balcao)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between gap-3 border border-edge p-3">
            <div className="min-w-0">
              <p className="truncate text-sm font-bold">{peca.descricao}</p>
              <p className="font-mono text-xs text-dim">{peca.sku} · balcão {brl(peca.preco_balcao)}</p>
            </div>
            <button type="button" className="shrink-0 text-xs text-erro"
                    onClick={() => { setPeca(null); setTermo(''); }}>
              trocar
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="rotulo" htmlFor="pr-pct">Desconto (%)</label>
              <input id="pr-pct" className="campo" inputMode="decimal" placeholder="15"
                     value={pct} onChange={(e) => setPct(e.target.value)} autoFocus />
            </div>
            <div>
              <label className="rotulo" htmlFor="pr-ini">Começa</label>
              <input id="pr-ini" type="date" className="campo" value={inicio} min={hojeISO()}
                     onChange={(e) => setInicio(e.target.value)} />
            </div>
            <div>
              <label className="rotulo" htmlFor="pr-fim">Termina</label>
              <input id="pr-fim" type="date" className="campo" value={fim} min={inicio}
                     onChange={(e) => setFim(e.target.value)} />
            </div>
          </div>

          {previa && (
            <p className="text-sm">
              De <span className="text-dim line-through">{brl(previa.de)}</span> por{' '}
              <strong className="text-lg">{brl(previa.por)}</strong> — congelado assim
              até {fim ? dataBR(fim) : 'o fim da vigência'}.
            </p>
          )}
          {salvar.error ? <Aviso tipo="erro">{mensagemDeErro(salvar.error)}</Aviso> : null}

          <div className="flex gap-2">
            <button type="button" className="botao-secundario flex-1 py-2 text-sm" onClick={aoFechar}>Voltar</button>
            <button type="button" className="botao-primario flex-1 py-2 text-sm disabled:opacity-50"
                    disabled={salvar.isPending || !previa || !fim}
                    onClick={() => salvar.mutate()}>
              {salvar.isPending ? 'Salvando…' : 'Colocar em promoção'}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
